import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, ArrowRight, BookOpen, Tag } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  "Career Guide": "text-[#38b6ff] bg-[#38b6ff]/10 border-[#38b6ff]/30",
  "Industry News": "text-green-400 bg-green-400/10 border-green-400/30",
  "Tips": "text-yellow-400 bg-yellow-400/10 border-yellow-400/30",
};

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    axios.get(`${API}/posts`)
      .then(r => setPosts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = ["all", ...new Set(posts.map(p => p.category))];
  const filtered = selectedCategory === "all" ? posts : posts.filter(p => p.category === selectedCategory);

  return (
    <div className="bg-[#0D0D0D] pt-16 lg:pt-20">
      {/* Hero */}
      <section className="py-20 md:py-28 relative overflow-hidden" data-testid="blog-hero">
        <div className="absolute inset-0 grid-pattern"></div>
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#38b6ff]/10 rounded-full blur-[180px]"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-xs font-bold uppercase tracking-[0.2em] text-[#38b6ff] mb-6"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              <span className="inline-flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Resources & Insights
              </span>
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-none text-[#F5F5F7] mb-6"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
              data-testid="blog-heading"
            >
              Blog & <span className="gradient-text">Resources</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg text-[#E0E0E0] leading-relaxed"
            >
              Career guides, industry insights, and everything you need to know about the court reporting profession.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Category filter */}
      <section className="bg-section-dark border-y border-[#1e2430] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 text-sm border transition-all ${
                  selectedCategory === cat
                    ? "border-[#38b6ff] bg-[#38b6ff]/10 text-[#38b6ff]"
                    : "border-[#333333] text-[#B0B0B0] hover:border-[#38b6ff]/50"
                }`}
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
                data-testid={`blog-filter-${cat.toLowerCase().replace(/\s/g, '-')}`}
              >
                {cat === "all" ? "All Posts" : cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts grid */}
      <section className="py-16 md:py-24" data-testid="blog-posts">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20 text-[#B0B0B0]">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-[#B0B0B0]">
              <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No posts yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((post, i) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Link
                    to={`/blog/${post.slug}`}
                    className="glass-card p-6 block h-full group"
                    data-testid={`blog-post-card-${post.slug}`}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <span className={`text-xs px-2 py-1 border rounded ${CATEGORY_COLORS[post.category] || "text-[#B0B0B0] bg-white/5 border-white/10"}`}>
                        <Tag className="w-3 h-3 inline mr-1" />{post.category}
                      </span>
                    </div>
                    <h2
                      className="text-lg font-bold text-[#F5F5F7] mb-3 group-hover:text-[#38b6ff] transition-colors"
                      style={{ fontFamily: 'Unbounded, sans-serif' }}
                    >
                      {post.title}
                    </h2>
                    <p className="text-sm text-[#B0B0B0] leading-relaxed mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t border-white/10">
                      <div className="flex items-center gap-2 text-xs text-[#B0B0B0]">
                        <Calendar className="w-3 h-3" />
                        {new Date(post.created_at).toLocaleDateString()}
                      </div>
                      <span className="text-[#38b6ff] text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
